import { useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useScope } from "@/api/queries";
import { useT } from "@/i18n/I18nProvider";
import { Notice } from "@/features/drill/sessionPanel";
import { DebugTerminal } from "./DebugTerminal";
import { useExecWindowClaim } from "./execWindow";

/**
 * 별도 Window의 Debug Terminal (ADR 0036 결정 5)
 * --------------------------------------------------------------------------
 * 앱 셸 **밖**의 라우트입니다. 작은 창에 좌측 nav와 헤더를 실으면 터미널이 설 자리가
 * 없고, 그 창에서 다른 화면으로 이동할 일도 없습니다.
 *
 * 대상은 URL이 싣습니다(`/debug-terminal/:name?ns=&uid=`) — 창은 부모의 메모리를
 * 물려받지 않으므로 Pod 신원이 링크에 있어야 합니다. **UID가 신원입니다**: 이름만으로는
 * 재생성된 Pod에 붙을 수 있고, 서버도 UID로 대상을 확정합니다.
 *
 * 클러스터는 URL에 싣지 않습니다(#94). Observer는 단일 클러스터 배포라 고르는 값이
 * 아니고, 이 창도 셸과 같은 방식으로 scope의 첫 접근 가능 클러스터를 씁니다.
 */

export const DEBUG_TERMINAL_WINDOW_PATH = "/debug-terminal/:namespace/:name";

export function DebugTerminalWindow() {
  const { namespace = "", name = "" } = useParams();
  const [params] = useSearchParams();
  const podUid = params.get("uid") ?? "";
  const { t } = useT("terminal");

  const scope = useScope();
  const cluster = scope.data?.clusters.find((c) => c.accessible) ?? scope.data?.clusters[0];
  const clusterId = cluster?.id ?? "";

  /* 이 창이 이 Pod의 세션 자리를 잡았다고 알립니다. 원래 화면은 그 신호를 받고 세션을
     놓은 뒤 "별도 Window 에서 연결되었습니다"만 말합니다. */
  useExecWindowClaim(podUid);

  /* 창 제목으로 어느 Pod의 터미널인지 알아볼 수 있어야 합니다 — 창이 여럿 열린 작업
     표시줄에서 제목이 전부 같으면 고를 수가 없습니다. Pod 이름은 번역하지 않습니다. */
  useEffect(() => {
    if (!name) return;
    const previous = document.title;
    document.title = `${t("terminal.title")} — ${name}`;
    return () => {
      document.title = previous;
    };
  }, [name, t]);

  if (!namespace || !podUid) {
    /* 신원이 없는 링크입니다. 서버에 묻지 않습니다 — 물어도 어느 Pod인지 모릅니다. */
    return (
      <div className="exec-window">
        <Notice tone="warning" role="alert" testId="exec-window-invalid">{t("terminal.window.invalid")}</Notice>
      </div>
    );
  }

  if (!clusterId) {
    return (
      <div className="exec-window">
        <Notice role="status">{t("terminal.loading")}</Notice>
      </div>
    );
  }

  return (
    <DebugTerminal
      clusterId={clusterId}
      namespace={namespace}
      podName={name}
      podUid={podUid}
      variant="window"
      onWindowClose={() => window.close()}
    />
  );
}
